export interface IcsEventInput {
  id: number;
  title: string | null;
  eventTypeName: string;
  eventDate: string;
  eventTime: string | null;
  endDate?: string | null;
  endTime?: string | null;
  location: string | null;
  videoCallUrl: string | null;
  description: string | null;
}

export async function downloadEventIcs(event: IcsEventInput): Promise<void> {
  const pad = (n: number) => String(n).padStart(2, '0');
  const escapeIcs = (s: string) => s.replace(/\\/g, '\\\\').replace(/,/g, '\\,').replace(/;/g, '\\;');

  const dateParts = event.eventDate.split('-').map(Number);
  const year = dateParts[0] ?? 2000;
  const month = dateParts[1] ?? 1;
  const day = dateParts[2] ?? 1;
  const dateStr = `${year}${pad(month)}${pad(day)}`;
  const now = new Date();
  const dtstamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`;

  // Compute end date string; ignore endDate if it's before eventDate
  let endDateStr = dateStr;
  if (event.endDate && event.endDate >= event.eventDate) {
    const endParts = event.endDate.split('-').map(Number);
    const ey = endParts[0] ?? year;
    const em = endParts[1] ?? month;
    const ed = endParts[2] ?? day;
    endDateStr = `${ey}${pad(em)}${pad(ed)}`;
  }

  let dtstart: string;
  let dtend: string;
  if (event.eventTime) {
    const timeParts = event.eventTime.split(':').map(Number);
    const h = timeParts[0] ?? 0;
    const m = timeParts[1] ?? 0;
    dtstart = `DTSTART:${dateStr}T${pad(h)}${pad(m)}00`;
    if (event.endTime) {
      const endTimeParts = event.endTime.split(':').map(Number);
      const eh = endTimeParts[0] ?? h + 1;
      const em2 = endTimeParts[1] ?? m;
      dtend = `DTEND:${endDateStr}T${pad(eh)}${pad(em2)}00`;
    } else {
      dtend = `DTEND:${endDateStr}T${pad(h + 1)}${pad(m)}00`;
    }
  } else {
    dtstart = `DTSTART;VALUE=DATE:${dateStr}`;
    if (event.endDate && event.endDate >= event.eventDate) {
      // End date is inclusive in display but exclusive in iCal, so add 1 day
      const endParts = event.endDate.split('-').map(Number);
      const ey = endParts[0] ?? year;
      const em = endParts[1] ?? month;
      const ed = endParts[2] ?? day;
      const nextDay = new Date(Date.UTC(ey, em - 1, ed + 1));
      const nextStr = `${nextDay.getUTCFullYear()}${pad(nextDay.getUTCMonth() + 1)}${pad(nextDay.getUTCDate())}`;
      dtend = `DTEND;VALUE=DATE:${nextStr}`;
    } else {
      const nextDay = new Date(Date.UTC(year, month - 1, day + 1));
      const nextStr = `${nextDay.getUTCFullYear()}${pad(nextDay.getUTCMonth() + 1)}${pad(nextDay.getUTCDate())}`;
      dtend = `DTEND;VALUE=DATE:${nextStr}`;
    }
  }

  // Geocode via Nominatim for Apple Calendar map support
  let geoLine = '';
  if (event.location) {
    try {
      // Nominatim usage policy: identify the app via User-Agent, 1 req/s, cache results.
      // Cache key is lowercased location; null sentinel marks "no result" to avoid retry storms.
      const cacheKey = `ics-geo:${event.location.trim().toLowerCase()}`;
      const cached = sessionStorage.getItem(cacheKey);
      let firstResult: { lat: string; lon: string } | null = null;
      if (cached !== null) {
        firstResult = cached === '' ? null : (JSON.parse(cached) as { lat: string; lon: string });
      } else {
        const geoRes = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(event.location)}&format=json&limit=1`,
          {
            headers: {
              'Accept-Language': 'en',
              'User-Agent': 'Monksheets/1.0 (ops@topmonks.com)',
            },
            signal: AbortSignal.timeout(5_000),
          }
        );
        const geoData = (await geoRes.json()) as { lat: string; lon: string }[];
        firstResult = geoData[0] ?? null;
        sessionStorage.setItem(cacheKey, firstResult ? JSON.stringify(firstResult) : '');
      }
      if (firstResult) {
        const { lat, lon } = firstResult;
        // X-TITLE is a parameter value (not TEXT) — literal commas are fine; only escape double-quotes (RFC 6868)
        const xtitle = event.location.replace(/"/g, "^'");
        geoLine = `GEO:${lat};${lon}\r\nX-APPLE-STRUCTURED-LOCATION;VALUE=URI;X-APPLE-RADIUS=72;X-TITLE="${xtitle}":geo:${lat},${lon}`;
      }
    } catch {
      // Fall back gracefully if geocoding fails (timeout, network, parse)
    }
  }

  const name = event.title ?? event.eventTypeName;
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//MonkSheets//TeamEvents//EN',
    'BEGIN:VEVENT',
    `UID:team-event-${event.id}@monksheets`,
    `DTSTAMP:${dtstamp}`,
    dtstart,
    dtend,
    `SUMMARY:${name}`,
    event.location ? `LOCATION:${escapeIcs(event.location)}` : '',
    geoLine,
    event.videoCallUrl ? `URL:${event.videoCallUrl}` : '',
    event.videoCallUrl ? `CONFERENCE;VALUE=URI;FEATURE=VIDEO;LABEL=Video Call:${event.videoCallUrl}` : '',
    event.description ? `DESCRIPTION:${event.description.replace(/\n/g, '\\n')}` : '',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean).join('\r\n');

  const blob = new Blob([lines], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${name.replace(/\s+/g, '-')}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}
